import React, { useState, useRef, useEffect, useCallback } from 'react';
import jsQR from 'jsqr';
import { adminAPI } from '../services/api';

/**
 * VIP QR checker.
 *
 * Answers "will this card actually scan at the till?" without walking to one.
 * The QR is HMAC("<card>:::<card><YYYYMMDDHH>") and carries no card number, so
 * the server matches it against the mirrored directory using the same secret BC
 * validates with — see services/qrVerify.js.
 *
 * Scanning prefers the browser's built-in BarcodeDetector and falls back to
 * jsQR, because BarcodeDetector is not implemented in Chrome on Windows — the
 * exact machine this page is used on. The paste box always works.
 */

const box = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 14,
  padding: 18,
  boxShadow: 'var(--card-shadow)',
};

export default function QRCheckPage() {
  const [value, setValue] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState('');

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);

  // BarcodeDetector is not implemented in Chrome on Windows, so jsQR decodes
  // frames wherever it is missing. The camera itself is always available.
  const hasNativeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;
  const canvasRef = useRef(null);

  const check = useCallback(async (raw) => {
    const v = String(raw || '').trim();
    if (!v) return;
    setChecking(true);
    setError('');
    setResult(null);
    try {
      setResult(await adminAPI.verifyQR(v));
    } catch (e) {
      setError(e.message || 'Could not check that code.');
    } finally {
      setChecking(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  // Release the camera when leaving the page — a live stream left running keeps
  // the indicator light on and holds the device.
  useEffect(() => stopCamera, [stopCamera]);

  const startCamera = async () => {
    setCamError('');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setScanning(true);
      // The <video> only exists once scanning is true, so wait a tick for it.
      setTimeout(async () => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        const detector = hasNativeDetector
          // eslint-disable-next-line no-undef
          ? new window.BarcodeDetector({ formats: ['qr_code'] })
          : null;

        const readFrame = async () => {
          const v = videoRef.current;
          if (!v || !v.videoWidth) return null;
          if (detector) {
            const found = await detector.detect(v);
            return found && found.length ? found[0].rawValue : null;
          }
          // jsQR needs pixels, so draw the frame to an offscreen canvas.
          const c = canvasRef.current || (canvasRef.current = document.createElement('canvas'));
          c.width = v.videoWidth;
          c.height = v.videoHeight;
          const ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(v, 0, 0, c.width, c.height);
          const img = ctx.getImageData(0, 0, c.width, c.height);
          const hit = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
          return hit ? hit.data : null;
        };

        loopRef.current = setInterval(async () => {
          try {
            const text = await readFrame();
            if (text) {
              setValue(text);
              stopCamera();
              check(text);
            }
          } catch { /* a frame that cannot be decoded is normal */ }
        }, 250);
      }, 0);
    } catch (e) {
      setCamError(
        e && e.name === 'NotAllowedError'
          ? 'Camera permission was refused. Allow it in the address bar, or paste the code below.'
          : 'Could not open the camera. Paste the code below instead.'
      );
    }
  };

  const m = result && result.member;

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 26 }}>Check a VIP QR</h1>
      <p style={{ margin: '5px 0 20px', color: 'var(--text-muted)', fontSize: 13.5 }}>
        Scan a member&rsquo;s code, or paste it, to see who it belongs to and whether the till would accept it.
      </p>

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Scan</div>

          {!scanning && (
            <button className="btn-primary" onClick={startCamera}>Open camera</button>
          )}

          {scanning && (
            <>
              <video
                ref={videoRef}
                muted
                playsInline
                style={{ width: '100%', borderRadius: 10, background: '#000', maxHeight: 260, objectFit: 'cover' }}
              />
              <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn-ghost" onClick={stopCamera}>Stop</button>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Point the camera at the QR…</span>
              </div>
            </>
          )}

          {camError && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--danger)' }}>{camError}</div>
          )}


          <div style={{ marginTop: 16, fontWeight: 700, marginBottom: 8 }}>Or paste it</div>
          <textarea
            rows={3}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="64 hex characters, e.g. 9e50df135e29d7fe…"
            style={{
              width: '100%', boxSizing: 'border-box', padding: 11, borderRadius: 10,
              border: '1px solid var(--border)', fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: 12, resize: 'vertical',
            }}
          />
          <button
            className="btn-primary"
            style={{ marginTop: 10 }}
            onClick={() => check(value)}
            disabled={checking || !value.trim()}
          >
            {checking ? 'Checking…' : 'Check this code'}
          </button>
        </div>

        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Result</div>

          {error && <div style={{ color: 'var(--danger)', fontSize: 13.5 }}>{error}</div>}

          {!result && !error && (
            <div style={{ color: 'var(--text-subtle)', fontSize: 13.5 }}>
              Nothing checked yet.
            </div>
          )}

          {result && !result.matched && (
            <div>
              <div style={{
                display: 'inline-block', padding: '4px 12px', borderRadius: 999,
                background: 'var(--danger-bg)', color: 'var(--danger)',
                fontWeight: 700, fontSize: 12, marginBottom: 10,
              }}>
                No match
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{result.reason}</div>
              {result.cardsChecked != null && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                  Checked against {Number(result.cardsChecked).toLocaleString()} card numbers.
                </div>
              )}
            </div>
          )}

          {result && result.matched && m && (
            <div>
              <div style={{
                display: 'inline-block', padding: '4px 12px', borderRadius: 999,
                background: result.wouldBeAccepted ? '#E7F6EC' : 'var(--danger-bg)',
                color: result.wouldBeAccepted ? '#1B7F3B' : 'var(--danger)',
                fontWeight: 700, fontSize: 12, marginBottom: 12,
              }}>
                {result.wouldBeAccepted ? 'Valid — the till would accept this' : 'The till would refuse this'}
              </div>

              {result.note && (
                <div style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 12 }}>{result.note}</div>
              )}

              <Row label="Name"    value={m.name || '—'} />
              <Row label="Card no" value={m.cardNo} mono />
              <Row label="Scheme"  value={m.scheme || '—'} />
              <Row label="Phone"   value={m.phone || '—'} mono />
              <Row label="Expiry"  value={m.expiryDate ? new Date(m.expiryDate).toLocaleDateString('en-GB') : '—'} />
              <Row label="Has the app" value={m.hasApp ? 'yes' : 'no'} />
              {m.blocked && <Row label="Blocked in BC" value="yes" />}
              {result.hourOffset !== 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
                  Matched the {result.hourOffset > 0 ? 'next' : 'previous'} hour&rsquo;s code — the value rolls hourly,
                  so a clock a little out of step still scans.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #F1F5F9' }}>
      <div style={{ width: 110, color: 'var(--text-muted)', fontSize: 12.5, flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13.5, fontFamily: mono ? 'ui-monospace, Menlo, monospace' : undefined, wordBreak: 'break-all' }}>
        {value}
      </div>
    </div>
  );
}
